import { useAssessmentDropdownFilters } from '@hooks/useAssessmentDropdownFilters'
import { useQuery } from '@tanstack/react-query'
import { GetAPIparams, handlePrint } from '@utils/assessmentReportUtils'
import { Flex, Spin } from 'antd'
import { useState } from 'react'

import { useLocation, useNavigate } from '@suntex.core/nav'
import { PlayerId } from '@suntex.fim/api-client'
import { AlgebraGoalsIndexChart } from '@suntex.fim/assessment'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { EntityLevel } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { Button } from '@suntex.fim/ui'

import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'

/**
 * Component for rendering the summary of the algebra goals metric in the assessment report.
 */
function AssessmentReportAlgebraGoalsMetric() {
  const { api } = useFramework()
  const location = useLocation()
  const { login } = useAuth(isAuthenticated)
  // Fetch team information from Redux store
  const isPlayerRole = location.pathname.includes('player')
  // Fetch team information from Redux store
  const team = useOptionalTeamInfo()
  const isSchool = location.pathname.includes('school')
  const fileName = `${team?.name || login.schoolName}_algebra_goals-index`
  const navigate = useNavigate()
  const [dropdownFilters, setDropDownFilters] = useAssessmentDropdownFilters()
  // State variables for loading state
  const [isExporting, setIsExporting] = useState(false)

  const { id, year, grade, level } = GetAPIparams(
    isSchool,
    dropdownFilters,
    isPlayerRole
  )

  const { data: playerData, isLoading } = useQuery({
    queryKey: ['assessment', 'playerSummary', id],
    queryFn: () =>
      api.get_Assessment_PlayerSummary({
        playerId: id as PlayerId
      }),
    enabled: id != null && location.pathname.includes('player')
  })

  // Render component JSX
  return (
    <Flex vertical gap="20px" style={{ position: 'relative' }}>
      {/* Loader overlay when loading and popup not open */}
      {(isLoading || isExporting) && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(255,255,255,0.7)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Spin size="large" tip="Loading..." />
        </div>
      )}
      {/* Navigation component */}
      <AssessmentReportNavigation
        exportButton={
          !isPlayerRole && (
            <Flex gap={10} className="export-btn print-hidden">
              <Button
                shape="round"
                intent="secondary"
                onClick={() => {
                  if (isSchool) {
                    navigate(
                      `/school/${id}/assessment-report/goals-algebra-by-team`
                    )
                  } else {
                    navigate(
                      `/team/${id}/assessment-report/goals-algebra-by-player`
                    )
                  }
                }}
              >
                View Report by {isSchool ? 'Team' : 'Player'}
              </Button>
              <Button
                shape="round"
                intent="secondary"
                onClick={() => {
                  void handlePrint({
                    setIsLoading: setIsExporting,
                    fileName
                  })
                }}
              >
                View Printer Friendly Version
              </Button>
            </Flex>
          )
        }
        dropdownFilters={dropdownFilters}
        setDropDownFilters={setDropDownFilters}
        isSchool={isSchool || location.pathname.includes('school')}
        isPlayer={isPlayerRole}
        displayReport={
          login.entityLevel !== EntityLevel.Player &&
          !location.pathname.includes('player')
        }
        title="Algebra Goals Metric Report"
      />

      {/* Summary dashboard */}
      <AssessmentReportSummaryDashboard
        dropdownFilters={dropdownFilters}
        isSchool={isSchool || location.pathname.includes('school')}
        isPlayer={isPlayerRole}
        player={playerData}
      />

      {/* Main result section */}
      <div className="main-result">
        <Flex vertical className="tiles-container">
          {/* Goal Index chart */}
          <Flex vertical gap="20px" className="graph-tiles team-activity">
            <Flex vertical align="center" className="graph-tiles__graph">
              <AlgebraGoalsIndexChart
                id={id!}
                level={level}
                year={year!}
                grade={grade}
                showLegend
                showProgressBars
              />
            </Flex>
          </Flex>
        </Flex>
      </div>
    </Flex>
  )
}

export default AssessmentReportAlgebraGoalsMetric
